import { Card } from "@/components/ui/Card"
import { type Batch, otherBatchesOf } from "../batches"
import { type PushAction, monthsLabel, num } from "../data"
import { formatDate } from "../dates"
import ActionsSection from "./ActionsSection"
import BatchChart from "./BatchChart"
import BatchProgress from "./BatchProgress"
import MentionedBy, { type Mention } from "./MentionedBy"
import Section from "./Section"
import SitesTable from "./SitesTable"

interface Props {
  batch: Batch
  actions: PushAction[]
  mentionedBy: Mention[]
  onChange: (actions: PushAction[]) => void
}

// One batch (a product with one expiry date) on Expiring soon: how much of it
// has cleared, where its units sit, and the actions decided for it.
export default function BatchDetail({ batch, actions, mentionedBy, onChange }: Props) {
  const { product } = batch
  const others = otherBatchesOf(batch)

  return (
    <Card>
      <div className="px-6 py-5">
        <h2 className="text-xl font-semibold">{product.name}</h2>
        <div className="mt-0.5 text-sm text-muted-foreground">{[product.code, product.sku, product.category].filter(Boolean).join(" · ")}</div>
        <div className="mt-3 text-sm font-semibold">
          Expires {formatDate(batch.expires, { month: "short", day: "numeric", year: "numeric" })} · {monthsLabel(batch.monthsLeft)} left
        </div>
      </div>

      <Section title="Progress">
        <BatchProgress batch={batch} />
        <div className="mt-4">
          <BatchChart points={batch.points} />
        </div>
        <MentionedBy mentions={mentionedBy} />
      </Section>

      <Section title="Where it sits">
        <div className="overflow-hidden rounded-md border">
          <SitesTable rows={batch.sites.map((s) => ({ warehouse: s.warehouse, qty: s.qty, move: s.outPerDay }))} />
        </div>
        <div className="mt-2 text-sm">
          {others.length === 0
            ? "This is the only batch of this product on the list."
            : others.map((b) => (
                <div key={b.id}>
                  Other batch: <span className="font-semibold tabular-nums">{num(b.qty)}</span> expiring {formatDate(b.expires, { month: "short", day: "numeric", year: "numeric" })}
                </div>
              ))}
        </div>
      </Section>

      <ActionsSection actions={actions} giftable={product.giftable} onChange={onChange} />
    </Card>
  )
}
